import { useEffect } from "react";
import { Link } from "react-router-dom";
import { applyPageSeo } from "../../lib/seo";

type ComponentShowcase = {
  name: string;
  category: string;
  description: string;
  usage: string;
  stack: string[];
  accent: string;
  preview: "header" | "button" | "receipt" | "verification" | "metrics" | "footer" | "hero" | "social";
};

const componentGroups = [
  {
    title: "Structure",
    description: "Reliable wrappers, section rhythm, and layout primitives that keep emails readable across clients.",
    blocks: ["Container", "Section", "Row", "Column", "Spacer"],
  },
  {
    title: "Conversion",
    description: "Hero bands, CTA buttons, summary cards, and urgency modules for onboarding and transactional flows.",
    blocks: ["Header", "Hero", "Button", "Offer card", "Progress prompt"],
  },
  {
    title: "Trust",
    description: "Receipts, verification states, alerts, and account-detail blocks that make sensitive emails legible fast.",
    blocks: ["Receipt table", "Security alert", "Verification code", "Order summary"],
  },
  {
    title: "Retention",
    description: "Digest modules, metrics, recommendation rails, and footer navigation that keep lifecycle emails useful.",
    blocks: ["Metric grid", "Notification digest", "Recommendation row", "Footer links"],
  },
];

const showcase: ComponentShowcase[] = [
  {
    name: "Brand Header",
    category: "Structure",
    description: "A hero wrapper that carries brand identity, context, and the first CTA without overwhelming the email.",
    usage: "Best for welcomes, invites, receipts, and any email that needs immediate orientation.",
    stack: ["Preview", "Container", "Section", "Heading", "Text"],
    accent: "from-violet-500 via-indigo-500 to-sky-500",
    preview: "header",
  },
  {
    name: "Primary CTA Button",
    category: "Conversion",
    description: "High-contrast action button with generous spacing and a shape that survives client quirks.",
    usage: "Use it once per screenful for the main next step: verify, view receipt, join, or recover.",
    stack: ["Button", "Link"],
    accent: "from-emerald-500 via-lime-500 to-emerald-400",
    preview: "button",
  },
  {
    name: "Receipt Table",
    category: "Trust",
    description: "A compact label-value layout for pricing, billing, shipping, and status information.",
    usage: "Ideal for commerce and billing flows where skimmability matters more than ornament.",
    stack: ["Section", "Text", "Hr"],
    accent: "from-amber-400 via-orange-400 to-yellow-400",
    preview: "receipt",
  },
  {
    name: "Verification Code",
    category: "Trust",
    description: "Single-purpose code blocks that stay legible in dark and light client environments.",
    usage: "Use for sign-in, email verification, two-factor auth, and ephemeral recovery flows.",
    stack: ["Row", "Column", "Text"],
    accent: "from-indigo-500 via-blue-500 to-cyan-400",
    preview: "verification",
  },
  {
    name: "Metrics Grid",
    category: "Retention",
    description: "Small stat cards for streaks, subscription usage, or recap-style engagement emails.",
    usage: "Works well when users need a reason to open the app again based on visible progress.",
    stack: ["Row", "Column", "Text", "Section"],
    accent: "from-lime-500 via-green-500 to-emerald-400",
    preview: "metrics",
  },
  {
    name: "Footer Navigation",
    category: "Structure",
    description: "Low-noise navigation for help, settings, billing, and notification preferences.",
    usage: "Put only secondary routes here so the primary message stays clear.",
    stack: ["Hr", "Text", "Link"],
    accent: "from-slate-500 via-zinc-500 to-slate-400",
    preview: "footer",
  },
  {
    name: "Hero Banner",
    category: "Conversion",
    description: "A wide opening band for launch messaging, onboarding momentum, or progress prompts.",
    usage: "Useful when the email has one high-value story to tell before the detail blocks begin.",
    stack: ["Section", "Heading", "Text", "Button"],
    accent: "from-rose-500 via-fuchsia-500 to-orange-400",
    preview: "hero",
  },
  {
    name: "Social Digest",
    category: "Retention",
    description: "Reusable notification rows for mentions, follows, comments, and activity recaps.",
    usage: "Best for daily or weekly lifecycle emails that need multiple updates in one scan.",
    stack: ["Section", "Text", "Link"],
    accent: "from-sky-500 via-cyan-500 to-blue-400",
    preview: "social",
  },
];

const assemblyLanes = [
  {
    title: "Openers",
    description: "Set context quickly and establish trust in the first screenful.",
    blocks: ["Preview text", "Brand header", "Hero copy", "Primary CTA"],
  },
  {
    title: "Core content",
    description: "Deliver the payload: order items, invite details, digest rows, or account events.",
    blocks: ["Cards", "Receipt rows", "Metrics", "Verification code", "Recommendation modules"],
  },
  {
    title: "Closers",
    description: "Reduce support friction without distracting from the main action.",
    blocks: ["Secondary CTA", "Help links", "Preference management", "Legal footer"],
  },
];

const safetyPatterns = [
  {
    title: "One dominant action",
    copy: "Every showcase block assumes a single primary CTA. Secondary paths live in supporting copy or the footer.",
  },
  {
    title: "Readable at a glance",
    copy: "Block layouts bias toward short labels, obvious hierarchy, and enough whitespace to survive narrow clients.",
  },
  {
    title: "Brand-aware, not overfitted",
    copy: "The same primitives can flex between Shopify, Discord, Apple, or Notion just by swapping palette, spacing, and tone.",
  },
];

function PreviewFrame({ preview, accent }: Pick<ComponentShowcase, "preview" | "accent">) {
  const heroClass = `rounded-md bg-gradient-to-br ${accent}`;

  if (preview === "header") {
    return (
      <div className="rounded border border-white/80 bg-white/90 p-3 shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
        <div className="flex items-center justify-between">
          <div className="rounded-md bg-slate-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Brand
          </div>
          <div className="h-2.5 w-16 rounded-full bg-slate-200" />
        </div>
        <div className={`mt-4 px-4 py-5 ${heroClass}`}>
          <div className="h-2 w-20 rounded-full bg-white/55" />
          <div className="mt-3 h-3 w-40 rounded-full bg-white/90" />
          <div className="mt-2 h-3 w-32 rounded-full bg-white/70" />
        </div>
      </div>
    );
  }

  if (preview === "button") {
    return (
      <div className="rounded border border-white/80 bg-white/90 p-4 shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
        <div className="space-y-2">
          <div className="h-2.5 w-28 rounded-full bg-slate-900" />
          <div className="h-2.5 w-40 rounded-full bg-slate-200" />
        </div>
        <div className={`mt-5 h-11 rounded-full bg-gradient-to-r ${accent}`} />
      </div>
    );
  }

  if (preview === "receipt") {
    return (
      <div className="rounded border border-white/80 bg-white/90 p-4 shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={`receipt-row-${index}`} className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-b-0 last:pb-0">
              <div className="h-2.5 w-20 rounded-full bg-slate-200" />
              <div className={`h-2.5 rounded-full bg-gradient-to-r ${accent}`} style={{ width: 72 - index * 8 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (preview === "verification") {
    return (
      <div className="rounded border border-white/80 bg-white/90 p-3 shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
        <div className="grid grid-cols-6 gap-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={`digit-${index}`}
              className={`flex h-12 items-center justify-center rounded-md bg-gradient-to-br ${accent} text-sm font-semibold text-white`}
            >
              {index + 1}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (preview === "metrics") {
    return (
      <div className="rounded border border-white/80 bg-white/90 p-3 shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={`metric-${index}`} className="rounded border border-slate-100 bg-slate-50 p-2.5">
              <div className="h-2 w-14 rounded-full bg-slate-200" />
              <div className={`mt-3 h-3 rounded-full bg-gradient-to-r ${accent}`} style={{ width: 40 + index * 12 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (preview === "footer") {
    return (
      <div className="rounded border border-white/80 bg-white/90 p-4 shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
        <div className="h-px bg-slate-200" />
        <div className="mt-4 flex flex-wrap gap-2">
          {["Help", "Billing", "Settings"].map((item) => (
            <div key={item} className="rounded-md bg-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              {item}
            </div>
          ))}
        </div>
        <div className="mt-4 h-2 w-40 rounded-full bg-slate-200" />
      </div>
    );
  }

  if (preview === "hero") {
    return (
      <div className={`rounded-md px-5 py-6 shadow-[0_22px_56px_rgba(15,23,42,0.14)] bg-gradient-to-br ${accent}`}>
        <div className="h-2 w-20 rounded-full bg-white/60" />
        <div className="mt-4 h-3 w-40 rounded-full bg-white/95" />
        <div className="mt-2 h-3 w-28 rounded-full bg-white/70" />
        <div className="mt-5 h-10 w-32 rounded-full bg-white/95" />
      </div>
    );
  }

  return (
    <div className="rounded border border-white/80 bg-white/90 p-3 shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={`social-${index}`}
          className={`rounded border border-slate-100 bg-slate-50 px-3 py-2.5 ${index > 0 ? "mt-3" : ""}`}
        >
          <div className="flex items-center gap-3">
            <div className={`h-8 w-8 rounded-full bg-gradient-to-br ${accent}`} />
            <div className="flex-1">
              <div className="h-2.5 w-24 rounded-full bg-slate-900/90" />
              <div className="mt-2 h-2.5 w-40 rounded-full bg-slate-200" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DiscoverComponents() {
  useEffect(() => {
    return applyPageSeo({
      title: "Email Component Library",
      description: "Browse reusable email components — headers, buttons, receipts, verification codes, metrics, footers, and more. Build production-grade emails from proven building blocks.",
      path: "/discover/components",
      keywords: ["react email components", "email building blocks", "email headers", "email buttons", "email footers", "email templates"],
    });
  }, []);

  return (
    <section className="relative overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.14),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(236,72,153,0.12),_transparent_34%),linear-gradient(180deg,_#f8fafc_0%,_#ffffff_22%,_#f8fafc_100%)] py-10 sm:py-12">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[size:88px_88px] [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0.2))]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-600">
            Component discovery
          </p>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
            Building blocks for production emails.
          </h1>
          <p className="mt-5 max-w-2xl text-sm text-slate-600">
            The primitives behind receipts, invites, verification emails, and campaigns.
          </p>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <div className="rounded border border-white/80 bg-white/85 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur">
            <p className="text-sm font-medium text-slate-500">Showcase blocks</p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
              {showcase.length}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Core UI patterns mapped to real email jobs-to-be-done.
            </p>
          </div>
          <div className="rounded border border-white/80 bg-white/85 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur">
            <p className="text-sm font-medium text-slate-500">Component groups</p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
              {componentGroups.length}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Structure, trust, conversion, and retention surfaces.
            </p>
          </div>
          <div className="rounded border border-white/80 bg-white/85 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur">
            <p className="text-sm font-medium text-slate-500">Assembly stages</p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
              {assemblyLanes.length}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Compose openers, payload blocks, and closers with less guesswork.
            </p>
          </div>
        </div>

        <div className="mt-12 grid gap-4 lg:grid-cols-4">
          {componentGroups.map((group) => (
            <div
              key={group.title}
              className="rounded-md border border-slate-200/70 bg-white/90 p-5 shadow-[0_18px_48px_rgba(15,23,42,0.08)]"
            >
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-500">
                {group.title}
              </p>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                {group.description}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {group.blocks.map((block) => (
                  <span
                    key={block}
                    className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600"
                  >
                    {block}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 grid gap-6 xl:grid-cols-2">
          {showcase.map((item) => (
            <article
              key={item.name}
              className="overflow-hidden rounded-md border border-slate-200/70 bg-white/90 shadow-[0_24px_72px_rgba(15,23,42,0.09)]"
            >
              <div className="grid gap-0 lg:grid-cols-[1.05fr_1fr]">
                <div className={`bg-gradient-to-br ${item.accent} p-5 text-white`}>
                  <div className="flex items-center justify-between">
                    <span className="rounded-md bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]">
                      {item.category}
                    </span>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">
                      Preview
                    </span>
                  </div>
                  <div className="mt-6">
                    <PreviewFrame preview={item.preview} accent={item.accent} />
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-500">
                    {item.category}
                  </p>
                  <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                    {item.name}
                  </h2>
                  <p className="mt-4 text-sm leading-6 text-slate-600">
                    {item.description}
                  </p>
                  <p className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-700">
                    {item.usage}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {item.stack.map((part) => (
                      <span
                        key={part}
                        className="rounded-md bg-slate-950 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white"
                      >
                        {part}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-md border border-slate-200/70 bg-white/90 p-5 shadow-[0_24px_72px_rgba(15,23,42,0.09)]">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-500">
              Assemble an email
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
              Think in sections, not one-off screens.
            </h2>
            <div className="mt-8 space-y-5">
              {assemblyLanes.map((lane) => (
                <div
                  key={lane.title}
                  className="rounded-md border border-slate-200 bg-slate-50 px-5 py-5"
                >
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {lane.title}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {lane.description}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {lane.blocks.map((block) => (
                      <span
                        key={block}
                        className="rounded-md border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600"
                      >
                        {block}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md border border-slate-200/70 bg-slate-950 p-5 text-white shadow-[0_28px_80px_rgba(15,23,42,0.18)]">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-white/60">
              Email-safe defaults
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight">
              Design for inbox reality, not just the mockup.
            </h2>
            <div className="mt-8 space-y-4">
              {safetyPatterns.map((pattern) => (
                <div
                  key={pattern.title}
                  className="rounded-md border border-white/10 bg-white/5 px-5 py-5"
                >
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/60">
                    {pattern.title}
                  </p>
                  <p className="mt-3 text-sm leading-6 text-white/80">
                    {pattern.copy}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Conversion CTA */}
        <div className="mt-14 overflow-hidden rounded-md bg-[linear-gradient(135deg,#0f172a_0%,#111827_52%,#0c4a6e_100%)] px-8 py-10 text-white shadow-[0_30px_90px_-35px_rgba(2,6,23,0.7)] sm:px-10 sm:py-12">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-300">
              Build with these components
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
              Use these blocks in your own emails
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-slate-300">
              Start from 148+ templates built with these components, or describe what you need and let AI assemble the blocks for you.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/discover/templates"
                className="inline-flex items-center gap-2 rounded-md bg-white px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
              >
                Browse templates
              </Link>
              <Link
                to="/auth/signup?redirect=%2Fbuilder"
                className="inline-flex items-center gap-2 rounded-md border-2 border-white/40 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white transition hover:border-white/60 hover:bg-white/15"
              >
                Start building free
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
